import React, { useState, useContext } from 'react'
import styled from 'styled-components'
import { Button, Input, Tooltip } from '@giphy/react-giphy-brand'
import { giphyRed } from '@giphy/js-brand'
import Checkbox from 'shared/components/checkbox'
import { without, concat } from 'lodash'
import { reportSearch } from 'shared/api/index'
import MessageContext from 'shared/components/context/message'

const REASONS_ORDER = [
    'NOT_RELEVANT',
    'NOT_FUN',
    'NOT_SAFE',
    'NOT_TIMELY',
    'NOT_ENOUGH_VERIFIED_CONTENT',
    'NOT_DIVERSE',
]

const REASONS_DESCRIPTION = {
    NOT_RELEVANT: 'It is not relevant',
    NOT_FUN: 'It is not very fun',
    NOT_SAFE: 'It is unsafe',
    NOT_TIMELY: 'It is not timely',
    NOT_ENOUGH_VERIFIED_CONTENT: "It doesn't have enough verified or artist content in high positions",
    NOT_DIVERSE: "It doesn't reflect our Community Diversity Guidelines",
}

const Textarea = styled(Input)`
    margin: 0;
    height: 100px;
    textarea {
        width: 100%;
        margin: 0;
        font-size: 14px;
        font-weight: 400;
        line-height: normal;
        color: #000;
        padding: 10px;
        outline: none;
    }
`

const Modal = styled.div`
    position: fixed;
    width: 546px;
    height: 675px;
    background-color: #212121;
    padding: 45px 35px 35px 35px;
    z-index: 9999999;
    left: calc(50% - 273px);
    top: calc(50% - 337px);
`

const StyledCheckbox = styled(Checkbox)`
    margin-right: 5px;
`

const CheckboxContainer = styled.label<{ stacked: boolean }>`
    display: ${props => (props.stacked ? 'block' : 'inline-block')};
    padding: ${props => (props.stacked ? '3px 0' : '0 35px 0 0')};
    color: rgb(255, 255, 255);
    font-size: 14px;
    font-weight: bold;
`

const Heading = styled.div`
    color: rgb(207, 207, 207);
    font-size: 18px;
    font-weight: bold;
    margin-bottom: 15px;
`

const Term = styled.span`
    color: ${giphyRed};
`

const ReasonsList = styled.div`
    margin: 30px 0;
`

const AdditionalInfoInput = styled(Textarea)`
    margin-bottom: 25px;
    height: 68px;
`

const Actions = styled.div`
    margin-top: 20px;
`

const SubmitButton = styled(Button)`
    display: block;
    width: 100%;
`

const CancelButton = styled(Button)`
    display: block;
    width: 100%;
    background-color: #212121;
`

const ColorBar = styled.div`
    background: linear-gradient(63.434948822922024deg, #06ff99 0%, #6157ff 100%);
    height: 5px;
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
`

const CloseButton = styled.div`
    mask-image: url(/static/img/close.svg);
    background-color: white;
    position: absolute;
    right: 20px;
    top: 20px;
    width: 30px;
    height: 30px;
    cursor: pointer;
`

const Dim = styled.div`
    position: fixed;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background-color: black;
    opacity: 0.8;
    z-index: 9999;
`

const ContentType = styled.div`
    margin: 30px 0;
`

const RequiredTooltip = styled(Tooltip)`
    color: white;
    display: inline-block;
    padding-left: 13px;
`

const RequiredLabel = styled.div`
    height: 17px;
    color: rgb(82, 82, 82);
    font-size: 14px;
    font-weight: bold;
    line-height: 17px;
`

type Props = {
    type: 'gifs' | 'stickers'
    searchResponseId: string
    clipSearchResponseId: string
    searchTerm: string
    onClose: Function
}

const ReportSearch = ({ type, searchResponseId, clipSearchResponseId, searchTerm, onClose = () => {} }: Props) => {
    const { sendMessage } = useContext(MessageContext)
    const [selectedReasons, setSelectedReasons] = useState<string[]>([])
    const [selectedContentTypes, setSelectedContentTypes] = useState<string[]>([])
    const [anonymous, setAnonymous] = useState(false)
    const [additionalInfo, setAdditionalInfo] = useState('')

    const submitClicked = () => {
        for (const contentType of selectedContentTypes) {
            const responseId = contentType === 'clips' ? clipSearchResponseId : searchResponseId
            reportSearch(selectedReasons.join(','), anonymous, additionalInfo, responseId, searchTerm, contentType)
        }
        sendMessage(`Successfully submitted report!`)
        onClose()
    }

    const closeClicked = () => {
        onClose()
    }

    return (
        <>
            <Dim onClick={closeClicked} />
            <Modal>
                <ColorBar />
                <CloseButton onClick={closeClicked} />

                <Heading>
                    Why are you reporting the search for &quot;<Term>{searchTerm}</Term>&quot;?
                </Heading>

                <ReasonsList>
                    {REASONS_ORDER.map(key => (
                        <CheckboxContainer stacked={true} key={key}>
                            <StyledCheckbox
                                isChecked={selectedReasons.includes(key)}
                                onChange={(checked: boolean) =>
                                    setSelectedReasons(prev => (checked ? concat(prev, key) : without(prev, key)))
                                }
                            />
                            {REASONS_DESCRIPTION[key]}
                        </CheckboxContainer>
                    ))}
                </ReasonsList>

                <Heading>
                    What type of content are you reporting?
                    <RequiredTooltip label={'This section must be completed before submitting'} maxWidth={205}>
                        <RequiredLabel>Required</RequiredLabel>
                    </RequiredTooltip>
                </Heading>

                <ContentType>
                    <CheckboxContainer stacked={false}>
                        <StyledCheckbox
                            isChecked={selectedContentTypes.includes(type)}
                            onChange={(checked: boolean) => {
                                setSelectedContentTypes(prev => (checked ? concat(prev, type) : without(prev, type)))
                            }}
                        />
                        {type === 'stickers' ? 'Stickers' : 'GIFs'}
                    </CheckboxContainer>
                    <CheckboxContainer stacked={false}>
                        <StyledCheckbox
                            isChecked={selectedContentTypes.includes('clips')}
                            onChange={(checked: boolean) => {
                                setSelectedContentTypes(prev =>
                                    checked ? concat(prev, 'clips') : without(prev, 'clips')
                                )
                            }}
                        />
                        Clips
                    </CheckboxContainer>
                </ContentType>

                <Heading>Any additional information?</Heading>

                <AdditionalInfoInput
                    initialValue={additionalInfo}
                    inputProps={{
                        as: 'textarea',
                        placeholder: '',
                        onChange: evt => setAdditionalInfo(evt.target.value),
                    }}
                />

                <CheckboxContainer stacked={true}>
                    <StyledCheckbox isChecked={anonymous} onChange={(checked: boolean) => setAnonymous(checked)} /> Send
                    anonymous report
                </CheckboxContainer>

                <Actions>
                    <SubmitButton onClick={submitClicked} disabled={selectedContentTypes.length === 0}>
                        Submit
                    </SubmitButton>
                    <CancelButton onClick={closeClicked}>Cancel</CancelButton>
                </Actions>
            </Modal>
        </>
    )
}

export default ReportSearch
